'use client';
import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useQueryAll } from '../apolloClient';
import supabase from '../supabase';
import { useScreenSize } from "../hooks/ScreenSizeContext";
import { DateTime } from "luxon";

import Card from '@mui/material/Card';
import Grid from "@mui/material/Grid";
import TextField from '@mui/material/TextField';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import MDBox from './MDBox/MDBoxRoot';
import MDButton from './MDButton/MDButtonRoot';
import StyledBox from './StyleBox';
import setThemeObj from './../hooks/useThemeMUI';
import { SchoolTime } from '../utils/SchoolTime';
import * as types from '../gql/graphql';


const tableHeaders = [
  'Event ID', 'Class Name', 'Date', 'Capacity' , 'Instructors Scheduled' , 'Instructors Ondeck' ,'Students Scheduled', 'Students Waitlisted', 'Students Attended', 'Notes'
];

const rowKeys = [
  'id', 'class_name', 'date', 'capacity', 'instructor_count', 'instructor_ondeck_count', 'signed_count', 'waitlisted_count', 'attended_count', 'notes'
];

const formatEventRow = (event) => {
  return {
    id: event?.id || '',
    class_name: event?.name || '',
    date: event.date ? SchoolTime.fromISO(event.date).toDisplayFormat() : '',
    capacity: event?.capacity || '',
    instructor_count: event?.instructor_count || '',
    instructor_ondeck_count: event?.instructor_ondeck_count || '',
    signed_count: event?.signed_count || '',
    waitlisted_count: event?.waitlisted_count || '',
    attended_count: event?.attended_count || '',
    notes: event?.notes || ''
};
}

const EventsReport = () => {
  const today = SchoolTime.now();
  const oneMonthAgo = today.addMonths(-1);
  const oneMonthInFuture = today.addMonths(1);
  const start = oneMonthAgo.toDateTime().toISODate();
  const end = oneMonthInFuture.toDateTime().toISODate();

  const [fromInput, setFromInput] = useState(start);
  const [toInput, setToInput] = useState(end);
  const [dateFilter, setDateFilter] = useState({ from: oneMonthAgo, to: oneMonthInFuture });

  /*
  const { loading, error, data } = useQueryAll<types.Fs_EventsEdge>({
    query: getInstructorEventsQuery,
    variables: { from: dateFilter.from.toISOLocal(), to: dateFilter.to.toISOLocal() },
  });*/

  const [data, setData] = useState([]);

  useEffect(
    () => {
      const fetchData = async () => {
        let { data: rows, error } = await supabase
          .rpc('get_events_report', {
            from_date: dateFilter.from.toISOLocal(),
            to_date: dateFilter.to.toISOLocal()
          })
        if (error) console.error(error)
        else {
          setData(rows);
        }
      }
    fetchData();
    }
    , [dateFilter]
  )

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromInput(e.target.value);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToInput(e.target.value);
  };

  const handleRunReport = () => {
    const fromDate = SchoolTime.fromISODate(fromInput);
    const toDate = SchoolTime.fromISODate(toInput);
    setDateFilter({ from: fromDate, to: toDate });
  };

  const downloadCSV = (rows) => {
    const headers = tableHeaders.join(",");
    const csvRows = rows.map(row =>
      rowKeys.map(key => {
        const cell = row[key];
        // Escape quotes
        const safe = typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell;
        return safe;
      }).join(",")
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Event_report_${fromInput}_to_${toInput}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //if (loading) return <MDBox p={2}>Loading...</MDBox>;
  //if (error) return <MDBox p={2}>Error loading events</MDBox>;

  const allRows = [];

  for (const event of data) {
    allRows.push(formatEventRow(event)); // returns an array of rows
  }


  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Card>
          <MDBox component="main" px={4} py={4}>
            <StyledBox theme={setThemeObj} sx={{ marginBottom: 2 }}>
              <Grid container alignItems="flex-end" justifyContent="space-between">
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="From:"
                    type="date"
                    value={fromInput}
                    onChange={handleFromDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <TextField
                    label="To:"
                    type="date"
                    value={toInput}
                    onChange={handleToDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleRunReport}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Run Report
                  </MDButton>
                </Grid>

                <Grid item>
                  <MDButton
                    variant="contained"
                    color="secondary"
                    onClick={() => downloadCSV(allRows)}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Download
                  </MDButton>
                </Grid>
              </Grid>
            </StyledBox>

            <MDBox sx={{ maxHeight: '800px', overflowY: 'auto' }}>
              <MDBox component="table" sx={{ borderSpacing: "0px", width: "100%" }}>
                <MDBox
                  component="thead"
                  ownerState={{ variant: "contained", bgColor: "light" }}
                  sx={{
                    '& td': { fontSize: '12px' },
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1,
                  }}
                  borderRadius={2}
                >
                  <MDBox component="tr">
                    {tableHeaders.map(header => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </MDBox>
                </MDBox>

                <TableBody sx={{ '& td': { fontSize: '12px' } }}>
                  {allRows.map(event => {
                    return (
                      <TableRow key={event.id}>
                        {rowKeys.map(key => (
                          <TableCell key={key}>{event[key]}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
};

export default EventsReport;
